'use client';
import React, { useState } from 'react';
import { IRepository, ICommit, IPagination } from '@/types';
import { Row, Box, Card, Stack } from '@/ui/primatives';

import { IDirectoryDiffTreeResponse } from '@/lib/diffApi';
import useDirectoryDiffTree from './useDirectoryDiffTree';
import useDirectoryDiff from './useDirectoryDiff';
import DirectoryDiffTree from './DirectoryDiffTree';
import DirectoryDiff from './DirectoryDiff';
import Flash from '@/ui/Flash';

// TODO: Hack in case there's no parent commit - should be handled before caling this component
const getBaseHeadRefs = (commit: ICommit): string[] => {
  const baseRef = commit.parent_ids?.[0] || commit.id;
  const headRef = commit.id;
  return [baseRef, headRef];
};

const CommitDiffFolderItems: React.FC<{
  repository: IRepository;
  baseRef: string;
  headRef: string;
  authToken?: string;
  filePath?: string;
  onFolderSelect: (path: string) => void;
}> = ({ repository, baseRef, headRef, filePath = '', onFolderSelect }) => {
  const {
    status: status,
    isLoading: isLoading,
    isValidating: isValidating,
    response: response,
    error: error,
  } = useDirectoryDiff({
    repository: repository,
    base: baseRef,
    head: headRef,
    filePath: filePath,
  });

  if (error) {
    return (
      <Flash.Error>
        <Stack gap={10}>
          <h6>Your request has retunred an error:</h6>
          <p className="mono">{error.title || error.detail}</p>
          <div>The Oxen team has been notified of this issue and will be looking into it shortly.</div>
        </Stack>
      </Flash.Error>
    );
  }

  return response ? (
    <DirectoryDiff repository={repository} response={response} onFolderSelect={onFolderSelect} />
  ) : (
    <div>Loading...</div>
  );
};

export const CommitDiffView: React.FC<{
  repository: IRepository;
  baseRef: string;
  headRef: string;
  initialData?: IDirectoryDiffTreeResponse;
  authToken?: string;
}> = ({ repository, baseRef, headRef, initialData, authToken }) => {
  const [selectedPath, setSelectedPath] = useState<string>('');

  const {
    status: status,
    isLoading: isLoading,
    isValidating: isValidating,
    response: response,
    error: error,
  } = useDirectoryDiffTree({
    repository: repository,
    base: baseRef,
    head: headRef,
    initialData: initialData,
  });

  return (
    <Row gap={20}>
      <Box style={{ minWidth: 300, alignItems: 'stretch' }}>
        {response ? (
          <DirectoryDiffTree
            response={response}
            selectedDir={selectedPath}
            onFolderSelect={setSelectedPath}
            rootNodeName={repository.name}
          />
        ) : (
          <div>Loading...</div>
        )}
      </Box>
      <div style={{ flex: 1 }}>
        <CommitDiffFolderItems
          repository={repository}
          baseRef={baseRef}
          headRef={headRef}
          filePath={selectedPath}
          authToken={authToken}
          onFolderSelect={setSelectedPath}
        />
      </div>
    </Row>
  );
};

const DiffView: React.FC<{
  repository: IRepository;
  commit: ICommit;
  initialData?: IDirectoryDiffTreeResponse;
  authToken?: string;
}> = ({ repository, commit, initialData, authToken }) => {
  const [base, head] = getBaseHeadRefs(commit);

  return (
    <CommitDiffView
      repository={repository}
      baseRef={base}
      headRef={head}
      initialData={initialData}
      authToken={authToken}
    />
  );
};

export default DiffView;
